// Family Members Manager Component
// Parent interface to add/manage family members (kids ages 5-12) and view family PIN
// Requires Family Plan subscription

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, Copy, RefreshCw, Edit2, Trash2, Check, X,
  AlertCircle, Crown, Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import { familyPINService } from '../../api/familyPINService';
import {
  FamilyMember,
  KID_AVATARS,
  KID_COLORS,
  isValidKidAge
} from '../../types/familyMember';
import { useStatus } from '../../contexts/StatusContext';
import { useSubscription } from '../../contexts/SubscriptionContext';

const FamilyMembersManager: React.FC = () => {
  const { subscriptionTier } = useSubscription();

  // State
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [familyPIN, setFamilyPIN] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPINModal, setShowPINModal] = useState(false);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    member_name: '',
    member_age: 5,
    avatar_emoji: '😊',
    favorite_color: '#60A5FA'
  });

  // Check subscription tier
  const hasRequiredSubscription = subscriptionTier === 'family' || subscriptionTier === 'premium';

  useEffect(() => {
    if (hasRequiredSubscription) {
      loadFamilyMembers();
    }
  }, [hasRequiredSubscription]);

  const loadFamilyMembers = async () => {
    setLoading(true);
    try {
      const response = await familyPINService.getFamilyMembers();
      setMembers(response.members);
      setFamilyPIN(response.family_pin);
    } catch (error) {
      console.error('[FamilyMembersManager] Error loading members:', error);
      toast.error('Failed to load family members');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!formData.member_name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    if (!isValidKidAge(formData.member_age)) {
      toast.error('Age must be between 5 and 12');
      return;
    }

    try {
      const response = await familyPINService.createFamilyMember({
        member_name: formData.member_name.trim(),
        member_age: formData.member_age,
        member_type: 'child'
      });

      setMembers([...members, response.member]);
      toast.success(response.message);

      // Reset form
      setFormData({
        member_name: '',
        member_age: 5,
        avatar_emoji: '😊',
        favorite_color: '#60A5FA'
      });
      setShowAddModal(false);
    } catch (error) {
      console.error('[FamilyMembersManager] Error adding member:', error);
      toast.error('Failed to add family member');
    }
  };

  const handleUpdateMember = async () => {
    if (!editingMember) return;

    try {
      const response = await familyPINService.updateFamilyMember(editingMember.member_id, {
        member_name: formData.member_name.trim(),
        member_age: formData.member_age,
        avatar_emoji: formData.avatar_emoji,
        favorite_color: formData.favorite_color
      });

      setMembers(members.map(m =>
        m.member_id === editingMember.member_id ? response.member : m
      ));
      toast.success(response.message);
      setEditingMember(null);
    } catch (error) {
      console.error('[FamilyMembersManager] Error updating member:', error);
      toast.error('Failed to update family member');
    }
  };

  const handleDeleteMember = async (memberId: string, memberName: string) => {
    if (!window.confirm(`Remove ${memberName} from your family? Their task history will be preserved.`)) {
      return;
    }

    try {
      await familyPINService.deleteFamilyMember(memberId);
      setMembers(members.filter(m => m.member_id !== memberId));
      toast.success(`${memberName} removed from family`);
    } catch (error) {
      console.error('[FamilyMembersManager] Error deleting member:', error);
      toast.error('Failed to remove family member');
    }
  };

  const handleRegeneratePIN = async () => {
    if (!window.confirm('Generate a new family PIN? The old PIN will stop working immediately.')) {
      return;
    }

    try {
      const response = await familyPINService.regeneratePIN();
      setFamilyPIN(response.new_pin);
      toast.success(response.message);
      setShowPINModal(true);
    } catch (error) {
      console.error('[FamilyMembersManager] Error regenerating PIN:', error);
      toast.error('Failed to regenerate PIN');
    }
  };

  const handleCopyPIN = () => {
    navigator.clipboard.writeText(familyPIN);
    toast.success('Family PIN copied to clipboard!');
  };

  const startEdit = (member: FamilyMember) => {
    setEditingMember(member);
    setFormData({
      member_name: member.member_name,
      member_age: member.member_age,
      avatar_emoji: member.avatar_emoji || '😊',
      favorite_color: member.favorite_color || '#60A5FA'
    });
  };

  // Render subscription upgrade prompt
  if (!hasRequiredSubscription) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 text-center"
        >
          <Crown className="w-16 h-16 mx-auto mb-4 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Family Members Feature
          </h2>
          <p className="text-gray-600 mb-6">
            Add kids ages 5-12 to your family! They can view and complete their assigned tasks using a simple PIN login.
          </p>
          <div className="bg-white rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Requires Family Plan</h3>
            <ul className="text-left space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <span>Add up to 8 family members (kids ages 5-12)</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <span>Secure 4-digit PIN for easy kid login</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <span>Kid-friendly task view with celebrations</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <span>Track tasks by family member</span>
              </li>
            </ul>
          </div>
          <button
            onClick={() => window.location.href = '/subscription'}
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all"
          >
            Upgrade to Family Plan
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-8 h-8 text-purple-600" />
            Family Members
          </h1>
          <p className="text-gray-600 mt-1">Manage kids ages 5-12 in your family</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Member
        </button>
      </div>

      {/* Family PIN Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 mb-8 border border-blue-200"
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Family PIN</h3>
            <p className="text-sm text-gray-600 mb-4">
              Share this PIN with your kids so they can log in and see their tasks
            </p>
            <div className="flex items-center gap-3">
              <div className="bg-white px-6 py-3 rounded-xl font-mono text-3xl font-bold text-indigo-600 tracking-wider">
                {familyPIN || '****'}
              </div>
              <button
                onClick={handleCopyPIN}
                className="p-3 bg-white rounded-xl hover:bg-gray-50 transition-colors"
                title="Copy PIN"
              >
                <Copy className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>
          <button
            onClick={handleRegeneratePIN}
            className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-xl hover:bg-gray-50 transition-colors border border-gray-200"
          >
            <RefreshCw className="w-4 h-4" />
            Regenerate
          </button>
        </div>
      </motion.div>

      {/* Members List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full mx-auto" />
          <p className="text-gray-600 mt-4">Loading family members...</p>
        </div>
      ) : members.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gray-50 rounded-2xl p-12 text-center"
        >
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No family members yet</h3>
          <p className="text-gray-600 mb-6">
            Add kids ages 5-12 to get started with family task management
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors"
          >
            Add Your First Member
          </button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {members.map((member) => (
            <motion.div
              key={member.member_id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-purple-200 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-3xl"
                    style={{ backgroundColor: member.favorite_color || '#60A5FA' }}
                  >
                    {member.avatar_emoji || '😊'}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{member.member_name}</h3>
                    <p className="text-sm text-gray-600">Age {member.member_age}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEdit(member)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleDeleteMember(member.member_id, member.member_name)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Tasks Assigned</p>
                  <p className="font-semibold text-gray-900">{member.assigned_task_count || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500">Tasks Completed</p>
                  <p className="font-semibold text-green-600">{member.completed_task_count || 0}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {(showAddModal || editingMember) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => {
              setShowAddModal(false);
              setEditingMember(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingMember ? 'Edit Family Member' : 'Add Family Member'}
              </h2>

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={formData.member_name}
                    onChange={(e) => setFormData({ ...formData, member_name: e.target.value })}
                    placeholder="Emma"
                    maxLength={50}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all"
                  />
                </div>

                {/* Age */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Age (5-12)
                  </label>
                  <input
                    type="number"
                    min={5}
                    max={12}
                    value={formData.member_age}
                    onChange={(e) => setFormData({ ...formData, member_age: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all"
                  />
                </div>

                {/* Avatar Emoji */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Choose Avatar
                  </label>
                  <div className="grid grid-cols-8 gap-2">
                    {KID_AVATARS.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => setFormData({ ...formData, avatar_emoji: emoji })}
                        className={`p-2 text-2xl rounded-lg hover:bg-gray-100 transition-colors ${
                          formData.avatar_emoji === emoji ? 'bg-purple-100 ring-2 ring-purple-500' : ''
                        }`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Favorite Color */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Favorite Color
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {KID_COLORS.map((color) => (
                      <button
                        key={color.value}
                        onClick={() => setFormData({ ...formData, favorite_color: color.value })}
                        className={`h-12 rounded-lg transition-all ${
                          formData.favorite_color === color.value ? 'ring-2 ring-gray-800 ring-offset-2' : ''
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingMember(null);
                    }}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={editingMember ? handleUpdateMember : handleAddMember}
                    className="flex-1 px-4 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors"
                  >
                    {editingMember ? 'Update' : 'Add Member'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FamilyMembersManager;
